import React from 'react'
import BeforeAfterProjectLimitsSection from './BeforeAfterProjectLimitsSection'
import { PaymentInterval, RecurlyPrice } from './Recurly'
import { UpgradeTideways6, UpgradeTideways6Application } from './UpgradeTideways6'
import Summary from './Summary'

interface Props {
    recurlyPricing: any
    upgrade: UpgradeTideways6
    projectId: string
    actionUrl: string
    csrfToken: string
    isTrial: boolean
    isCheckout: boolean
    currentPrice?: number
}
interface StateProps {
    projectSize: number | null
    price: RecurlyPrice | null
}

class UpgradeProject extends React.Component<Props, StateProps> {
    constructor(props: Props) {
        super(props)

        this.state = { projectSize: this.props.upgrade.projectSizes[this.props.projectId], price: null }
    }
    public handleProjectSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ projectSize: parseInt(event.target.value, 10) })
    }

    public render() {
        const planLabels = {}
        this.props.upgrade.availablePlans.forEach(plan => {
            planLabels[plan.name] = plan.label
        })

        const application = this.props.upgrade.applications.find((app: UpgradeTideways6Application) => {
            return app.id === parseInt(this.props.projectId, 10)
        })

        if (!application) {
            return null
        }

        return (
            <div className="row">
                <form className="form-horizontal" method="POST" action={this.props.actionUrl}>
                    <div className="col-lg-7">
                        <input type="hidden" name="_csrf[billing]" value={this.props.csrfToken} />
                        <div className="box">
                            <div className="box-header">
                                <h3 className="box-title">Increase or Reduce Project Size</h3>
                            </div>
                            <div className="box-body">
                                <p>
                                    The project size, combined with the current plan, determines a range of different limits for a project.
                                </p>

                                <p>
                                    Purchase Limit Add-ons to increase the project size and all associated limits: services, transactions,
                                    environments, traces/minute and more.
                                </p>

                                <div className="form-group">
                                    <label className="col-lg-4 control-label">
                                        <label>Plan:</label>
                                    </label>
                                    <div className="col-lg-1 control-label">{planLabels[this.props.upgrade.newPlan]}</div>
                                </div>

                                <div className="form-group">
                                    <label className="col-lg-4 control-label" htmlFor="changeProjectSize">
                                        Change Project Size:
                                    </label>
                                    <div className="col-lg-1 control-label">{this.state.projectSize || application.projectSize}</div>
                                    <div className="col-lg-3">
                                        <input
                                            type="range"
                                            className="form-control"
                                            name="changeProjectSize"
                                            min="1"
                                            max={this.maxProjectSize()}
                                            value={this.state.projectSize || application.projectSize}
                                            onChange={this.handleProjectSizeChange}
                                        />
                                    </div>
                                </div>

                                <BeforeAfterProjectLimitsSection
                                    onlyAfter={false}
                                    upgrade={this.props.upgrade}
                                    projectId={this.props.projectId}
                                    projectSize={this.state.projectSize || application.projectSize}
                                    plan={this.props.upgrade.newPlan}
                                />
                            </div>
                            <div className={'box-footer'}>
                                <a className="btn btn-default" onClick={() => history.back()}>
                                    Go Back
                                </a>
                                <button type="submit" className="btn btn-primary pull-right">
                                    {this.props.isTrial ? 'Change Project Size' : 'Change Project Size with obligation to pay'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-5">
                        {this.props.isCheckout ? (
                            <div className="box">
                                <div className="box-header">
                                    <h3 className="box-title">Summary New Subscription Price</h3>
                                </div>
                                <div className="box-body">
                                    Please make your final adjustments to the project size before creating a subscription. Project sizes
                                    control the detail of monitoring and profiling data and affect the total price.
                                </div>
                            </div>
                        ) : this.props.isTrial ? (
                            <div className="box">
                                <div className="box-header">
                                    <h3 className="box-title">Summary New Subscription Price</h3>
                                </div>
                                <div className="box-body">During the trial you can change the project size freely.</div>
                            </div>
                        ) : (
                            <div>
                                <Summary
                                    recurlyPricing={this.props.recurlyPricing}
                                    upgrade={this.props.upgrade}
                                    newPlan={this.props.upgrade.newPlan}
                                    coupon={this.props.upgrade.coupon}
                                    currency={this.props.upgrade.currency}
                                    projectSizes={this.calculateProjectSizes()}
                                    interval={PaymentInterval.Monthly}
                                    showHeader={true}
                                    currentPrice={this.props.currentPrice}
                                />
                                <div className="box" style={{ paddingTop: '20px' }}>
                                    <div className="box-body">
                                        <p>Tideways subscriptions are subject to the following conditions:</p>

                                        <ul>
                                            <li>
                                                <a href="https://tideways.com/tos" target="_blank" rel="noreferrer">
                                                    Terms of Service
                                                </a>
                                            </li>
                                            <li>
                                                See detailed functionality matrix and prices on{' '}
                                                <a href="https://tideways.com/profiler/pricing" target="_blank" rel="noreferrer">
                                                    Plans, Features, Prices
                                                </a>
                                            </li>
                                            <li>
                                                All prices listed do not include Value Added Tax (VAT). Tideways GmbH is based in Germany,
                                                European VAT laws apply. Companies outside the EU don&apos;t pay VAT.
                                            </li>
                                            <li>
                                                You can cancel your subscription every month. Your account will continue to work until the
                                                billing period ends.
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </form>
            </div>
        )
    }

    // See also Plan::getMaximumProjectSize
    private maxProjectSize() {
        if (this.props.upgrade.newPlan === 'tideways6_basic') {
            return 5
        } else if (this.props.upgrade.newPlan === 'tideways7_basic') {
            return 5
        } else if (this.props.upgrade.newPlan === 'tideways6_starter') {
            return 1
        }

        return 30
    }

    private calculateProjectSizes() {
        const projectSizes = {}
        this.props.upgrade.applications.forEach(application => {
            projectSizes[application.id] = application.projectSize
        })

        projectSizes[this.props.projectId] = this.state.projectSize
        return projectSizes
    }
}

export default UpgradeProject
